import { useCallback, useEffect, useRef } from "react";

import * as intents from "../data/intents";
import { newActionId } from "../data/callables";
import type {
  PlayoffSeriesModel,
  ScheduleModel,
} from "../shared/types/contracts";
import { responseRevision } from "./commandTypes";
import type { EnqueueCommand, RunCommand } from "./commandTypes";
import type {
  PlayoffSeriesInput,
  PlayoffSeriesPatch,
  ScheduleInput,
  SchedulePatch,
  TeamCommands,
} from "./teamTypes";
import { actionKeys } from "./actionKeys";
import type { OptimisticScheduleController } from "./useOptimisticSchedule";
import { eventRecovery } from "./useSerializedGameQueue";

interface ScheduleCommandOptions {
  teamId: string;
  scheduleGames: ScheduleModel[];
  playoffSeries: PlayoffSeriesModel[];
  run: RunCommand;
  enqueue: EnqueueCommand;
  onScheduleSelected?: (scheduleId: string, committedRevision: number) => void;
  projectUpdate?: OptimisticScheduleController["projectUpdate"];
  markCommitted?: OptimisticScheduleController["markCommitted"];
  rejectProjection?: OptimisticScheduleController["reject"];
}

export type UpdateScheduleCommand = (
  scheduleId: string,
  patch: SchedulePatch,
  statusKey?: string,
  onCommitted?: (revision: number) => void,
) => Promise<boolean>;

export type ScheduleCommandController = Omit<
  Pick<
    TeamCommands,
    | "createSchedule"
    | "updateSchedule"
    | "selectSchedule"
    | "deleteSchedule"
    | "createSeries"
    | "updateSeries"
    | "deleteSeries"
  >,
  "updateSchedule"
> & { updateSchedule: UpdateScheduleCommand };

export function useScheduleCommands({
  teamId,
  scheduleGames,
  playoffSeries,
  run,
  enqueue,
  onScheduleSelected,
  projectUpdate,
  markCommitted,
  rejectProjection,
}: ScheduleCommandOptions): ScheduleCommandController {
  const scheduleRevisionRef = useRef(new Map<string, number>());
  useEffect(() => {
    for (const entry of scheduleGames) {
      const revision = entry.revision ?? 0;
      const current = scheduleRevisionRef.current.get(entry.id) ?? 0;
      scheduleRevisionRef.current.set(entry.id, Math.max(current, revision));
    }
  }, [scheduleGames]);

  const scheduleAction = useCallback(
    (
      operation: () => Promise<unknown>,
      key: string,
      actionId: string,
      scheduleId?: string,
      onSuccess?: (committedRevision: number) => void,
      acknowledgement?: Promise<void> | null,
      onChainFailure?: () => void,
    ): Promise<boolean> => {
      const recordCommitted = (committedRevision: number) => {
        if (scheduleId) {
          scheduleRevisionRef.current.set(scheduleId, committedRevision);
        }
        onSuccess?.(committedRevision);
      };
      return enqueue(
        () =>
          run(operation, (response) => {
            const committedRevision = responseRevision(response);
            if (committedRevision !== null) {
              recordCommitted(committedRevision);
            }
          }),
        {
          key,
          ...(scheduleId ? { chainKey: `schedule:${scheduleId}` } : {}),
          intentId: actionId,
          recovery: eventRecovery(
            teamId,
            actionId,
            scheduleId || onSuccess ? recordCommitted : undefined,
          ),
          ...(acknowledgement ? { acknowledgement } : {}),
          ...(onChainFailure ? { onChainFailure } : {}),
        },
      );
    },
    [enqueue, run, teamId],
  );

  const createSchedule = (schedule: ScheduleInput): Promise<boolean> => {
    const actionId = newActionId();
    return scheduleAction(
      () => intents.sendCreateSchedule({ teamId, schedule, actionId }),
      actionKeys.scheduleCreate,
      actionId,
    );
  };
  const updateSchedule: UpdateScheduleCommand = (
    scheduleId: string,
    patch: SchedulePatch,
    statusKey = actionKeys.scheduleUpdate(scheduleId),
    onCommitted,
  ): Promise<boolean> => {
    const entry = scheduleGames.find(
      (candidate) => candidate.id === scheduleId,
    );
    if (!entry) return Promise.resolve(false);
    const actionId = newActionId();
    const projection = projectUpdate?.(actionId, scheduleId, patch) ?? {
      acknowledgement: null,
    };
    return scheduleAction(
      () =>
        intents.sendUpdateSchedule({
          teamId,
          scheduleId,
          expectedRevision:
            scheduleRevisionRef.current.get(scheduleId) ?? entry.revision ?? 0,
          patch,
          actionId,
        }),
      statusKey,
      actionId,
      scheduleId,
      (committedRevision) => {
        markCommitted?.(actionId, committedRevision);
        onCommitted?.(committedRevision);
      },
      projection.acknowledgement,
      () => rejectProjection?.(actionId),
    );
  };
  const selectSchedule = (scheduleId: string): Promise<boolean> => {
    const actionId = newActionId();
    return scheduleAction(
      () => intents.sendSelectSchedule({ teamId, scheduleId, actionId }),
      actionKeys.scheduleSelect(scheduleId),
      actionId,
      undefined,
      (committedRevision) => {
        onScheduleSelected?.(scheduleId, committedRevision);
      },
    );
  };
  const deleteSchedule = (scheduleId: string): Promise<boolean> => {
    const entry = scheduleGames.find(
      (candidate) => candidate.id === scheduleId,
    );
    if (!entry) return Promise.resolve(false);
    const actionId = newActionId();
    return scheduleAction(
      () =>
        intents.sendDeleteSchedule({
          teamId,
          scheduleId,
          expectedRevision:
            scheduleRevisionRef.current.get(scheduleId) ?? entry.revision ?? 0,
          actionId,
        }),
      actionKeys.scheduleDelete(scheduleId),
      actionId,
      scheduleId,
    );
  };

  const playoffSeriesRevisionRef = useRef(new Map<string, number>());
  useEffect(() => {
    for (const series of playoffSeries) {
      const revision = series.revision ?? 0;
      const current = playoffSeriesRevisionRef.current.get(series.id) ?? 0;
      playoffSeriesRevisionRef.current.set(
        series.id,
        Math.max(current, revision),
      );
    }
  }, [playoffSeries]);

  const createSeries = (series: PlayoffSeriesInput): Promise<boolean> => {
    const actionId = newActionId();
    return enqueue(
      () =>
        run(() =>
          intents.sendCreatePlayoffSeries({ teamId, series, actionId }),
        ),
      {
        key: actionKeys.seriesCreate,
        intentId: actionId,
        recovery: eventRecovery(teamId, actionId),
      },
    );
  };
  const updateSeries = (
    seriesId: string,
    patch: PlayoffSeriesPatch,
  ): Promise<boolean> => {
    const series = playoffSeries.find((candidate) => candidate.id === seriesId);
    if (!series) return Promise.resolve(false);
    const actionId = newActionId();
    return enqueue(
      () =>
        run(
          () =>
            intents.sendUpdatePlayoffSeries({
              teamId,
              seriesId,
              expectedRevision:
                playoffSeriesRevisionRef.current.get(seriesId) ??
                series.revision ??
                0,
              patch,
              actionId,
            }),
          (response) => {
            const committedRevision = responseRevision(response);
            if (committedRevision !== null) {
              playoffSeriesRevisionRef.current.set(seriesId, committedRevision);
            }
          },
        ),
      {
        key: actionKeys.seriesUpdate(seriesId),
        chainKey: `series:${seriesId}`,
        intentId: actionId,
        recovery: eventRecovery(teamId, actionId),
      },
    );
  };
  const deleteSeries = (seriesId: string): Promise<boolean> => {
    const series = playoffSeries.find((candidate) => candidate.id === seriesId);
    if (!series) return Promise.resolve(false);
    const actionId = newActionId();
    return enqueue(
      () =>
        run(() =>
          intents.sendDeletePlayoffSeries({
            teamId,
            seriesId,
            expectedRevision:
              playoffSeriesRevisionRef.current.get(seriesId) ??
              series.revision ??
              0,
            actionId,
          }),
        ),
      {
        key: actionKeys.seriesDelete(seriesId),
        chainKey: `series:${seriesId}`,
        intentId: actionId,
        recovery: eventRecovery(teamId, actionId),
      },
    );
  };

  return {
    createSchedule,
    updateSchedule,
    selectSchedule,
    deleteSchedule,
    createSeries,
    updateSeries,
    deleteSeries,
  };
}
